import { useCallback } from 'react'
import { useAccountSettings } from './useAccountSettings'
import {
  formatDate as formatDateWith,
  formatDateTime as formatDateTimeWith,
  formatTime as formatTimeWith,
  DEFAULT_DATE_FORMAT,
  DEFAULT_TIMEZONE,
  DEFAULT_CALENDAR,
  type CalendarSystem,
} from '@/lib/date'

// Resolve the account's localization config, preferring the top-level
// serializer fields (the documented contract) and falling back to the legacy
// `settings` hash, then to sensible defaults. Exported so non-date callers
// (e.g. the billing-period dropdown labels) can honour the configured
// calendar era without re-deriving the fallback chain.
export function useLocalizationConfig() {
  const { data } = useAccountSettings()
  const format = data?.date_format ?? data?.settings?.date_format ?? DEFAULT_DATE_FORMAT
  const timezone = data?.timezone ?? data?.settings?.timezone ?? DEFAULT_TIMEZONE
  const calendar = (data?.calendar ??
    data?.settings?.calendar ??
    DEFAULT_CALENDAR) as CalendarSystem
  return { format, timezone, calendar }
}

/**
 * Returns a `formatDate(isoString)` bound to the account's date format,
 * timezone, and calendar (Gregorian/Buddhist) — so all on-screen dates render
 * the way the user configured in Settings, not the browser's system locale.
 */
export function useDateFormatter() {
  const { format, timezone, calendar } = useLocalizationConfig()
  return useCallback(
    (dateString: string | null | undefined) =>
      formatDateWith(dateString, { format, timezone, calendar }),
    [format, timezone, calendar],
  )
}

/**
 * Like useDateFormatter but appends 24-hour HH:MM in the account timezone.
 * For audit-style datetime fields (signed at, expires at).
 */
export function useDateTimeFormatter() {
  const { format, timezone, calendar } = useLocalizationConfig()
  return useCallback(
    (dateString: string | null | undefined) =>
      formatDateTimeWith(dateString, { format, timezone, calendar }),
    [format, timezone, calendar],
  )
}

/**
 * Returns a `formatTime(isoString)` that renders just 24-hour HH:MM in the
 * account timezone (no date). For time-of-day columns (e.g. parking logs).
 */
export function useTimeFormatter() {
  const { timezone } = useLocalizationConfig()
  return useCallback(
    (dateString: string | null | undefined) => formatTimeWith(dateString, { timezone }),
    [timezone],
  )
}
